import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse, Canceler } from 'axios';
import { isEmbedContext } from 'src/components/embed/utils';
import { clearAccessToken, getToken } from 'src/helpers/token';


const { CancelToken } = axios;
const cancel: Record<string, Canceler> = {};

export type OnErrorHandler = (
  route: string | undefined,
  error: unknown,
  hideModal?: () => void,
) => unknown;

export interface RequestOptions {
  method?: string;
  url?: string;
  params?: Record<string, unknown> | FormData | null | unknown;
  is_authenticated?: boolean;
  content_type?: string;
  cancel_token?: string | null;
  route?: string;
  hideModal?: () => void;
  onError?: OnErrorHandler;
}

// Formate Request Options
export function formatRequest(
  method: string = 'GET',
  url: string | null = null,
  params: Record<string, unknown> | FormData | null | unknown = null,
  isAuthenticated: boolean = true,
  contentType: string = 'json',
  cancelToken: string | null = null,
): AxiosRequestConfig {
  const requestOptions: AxiosRequestConfig & { credentials?: string } = {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    credentials: 'include',
    method,
    url: `${process.env.NEXT_PUBLIC_APP_API_URL}${url}`,
  };
  if (isAuthenticated) {
    const token = getToken();
    if (token) {
      (requestOptions.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
  }

  if (method === 'GET' && params) {
    requestOptions.params = params;
  } else if (params instanceof FormData) {
    // Remove Content-Type header for FormData to let browser set it automatically
    delete (requestOptions.headers as Record<string, string>)['Content-Type'];
    requestOptions.data = params;
  } else if (contentType === 'json') {
    (requestOptions.headers as Record<string, string>)['Content-Type'] = 'application/json';
    requestOptions.data = params;
  } else {
    requestOptions.data = params;
  }
  if (cancelToken) {
    requestOptions.cancelToken = new CancelToken((c: Canceler) => {
      cancel[cancelToken] = c;
    });
  }

  return requestOptions;
}

// Handle Error  Response
export function handleError(
  _route: string | undefined,
  error: unknown,
  hideModal?: () => void,
): Promise<never> {
  if (error && (error as { response?: { status?: number } }).response) {
    if ((error as { response: { status: number } }).response.status === 401) {
      if (hideModal) {
        hideModal();
      }
      // clearAccessToken() is already context-aware: inside the extension embed
      // iframe it clears only the in-memory embed token, and outside it removes
      // USER_ACCESS_TOKEN_KEY for an admin impersonation session. Clearing
      // 'userAccessToken' unconditionally defeated that guard — an expired
      // extension token 401s here, and localStorage is shared across every tab
      // on this origin, so it logged out an unrelated admin "login as customer"
      // session in another tab.
      clearAccessToken();

      // Same reason: in the embed the machine already renders <SessionExpired />
      // and notifies the extension, so navigating the iframe to /auth would only
      // replace the panel contents with the portal login page.
      if (!isEmbedContext()) {
        window.location.assign('/auth');
      }
    }
  }
  return Promise.reject(error);
}

// Handle Response
export function handleResponse(
  _route: string | undefined,
  response: AxiosResponse,
): AxiosResponse {
  return response;
}

// Common Request
export async function request(options: RequestOptions): Promise<AxiosResponse> {
  if (options.cancel_token && cancel[options.cancel_token]) {
    cancel[options.cancel_token]();
  }
  try {
    const response = await axios(
      formatRequest(
        options.method,
        options.url,
        options.params,
        options.is_authenticated,
        options.content_type,
        options.cancel_token,
      ),
    );
    return handleResponse(options.route, response);
  } catch (error) {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }
    const onError = options.onError ?? handleError;
    return onError(options.route, error, options.hideModal) as Promise<AxiosResponse>;
  }
}

export default request;
